import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Mail, Lock, Eye, Database } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="privacy-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3 mb-8">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-300 bg-clip-text text-transparent">
              Politique de Confidentialité
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm">
            Protection de vos données personnelles
          </p>
        </div>

        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardContent className="p-6 md:p-8 space-y-6 text-sm leading-relaxed">
            
            {/* Introduction */}
            <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-cyan-400 mt-1" />
                <div>
                  <p className="font-semibold text-cyan-300 mb-2">Engagement de Confidentialité</p>
                  <p className="text-[hsl(var(--text-dim))]">
                    Dans une logique de respect de la vie privée de ses Utilisateurs, OpenMeta s'engage à ce que la collecte et le traitement d'informations personnelles soient effectués conformément à la loi n° 78-17 du 6 janvier 1978 relative à l'informatique, aux fichiers et aux libertés modifiée par la loi n° 2004-801 du 6 août 2004, ainsi qu'au RGPD (Règlement Général sur la Protection des Données).
                  </p>
                </div>
              </div>
            </div>

            {/* 1. Responsable du traitement */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3 flex items-center gap-2">
                <Database className="w-6 h-6" />
                1. Responsable du Traitement des Données
              </h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p><strong className="text-[hsl(var(--text))]">OpenMeta</strong> assume la qualité de responsable de traitement au sens de la législation applicable relative à la protection des données à caractère personnel.</p>
                
                <div className="mt-3 p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                  <p className="font-semibold text-[hsl(var(--text))] mb-2">Coordonnées :</p>
                  <p>Centre de l'innovation technologique</p>
                  <p>61330 Céaucé, FRANCE</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Mail className="w-4 h-4 text-cyan-400" />
                    <a href="mailto:support@openmeta.fr" className="text-cyan-400 hover:underline">support@openmeta.fr</a>
                  </div>
                </div>
              </div>
            </section>

            {/* 2. Données collectées */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3 flex items-center gap-2">
                <Eye className="w-6 h-6" />
                2. Données Collectées
              </h2>
              <div className="space-y-3 text-[hsl(var(--text-dim))]">
                <p>Dans le cadre de l'utilisation de l'Application UrgenceMed, les données suivantes peuvent être collectées :</p>
                
                <div className="space-y-2 ml-4">
                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-[hsl(var(--text))] mb-1">Données d'identification :</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Nom et prénom</li>
                      <li>Adresse email</li>
                      <li>Mot de passe (hashé et sécurisé)</li>
                      <li>Profession médicale</li>
                    </ul>
                  </div>

                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-[hsl(var(--text))] mb-1">Données d'utilisation :</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Historique des consultations</li>
                      <li>Favoris enregistrés</li>
                      <li>Préférences de l'application</li>
                    </ul>
                  </div>

                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-[hsl(var(--text))] mb-1">Données techniques :</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Adresse IP</li>
                      <li>Type d'appareil et système d'exploitation</li>
                      <li>Logs de connexion</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. Finalités du traitement */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">3. Finalités du Traitement</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>Les données collectées sont traitées pour les finalités suivantes :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Création et gestion de votre compte utilisateur</li>
                  <li>Fourniture des services de l'application</li>
                  <li>Personnalisation de votre expérience</li>
                  <li>Communication d'informations relatives aux services</li>
                  <li>Amélioration de l'application et des services</li>
                  <li>Respect des obligations légales</li>
                  <li>Analyse statistique anonymisée</li>
                </ul>
              </div>
            </section>

            {/* 4. Base légale */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">4. Base Légale du Traitement</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>Le traitement de vos données repose sur les bases légales suivantes :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>Consentement</strong> : acceptation des CGU lors de la création du compte</li>
                  <li><strong>Exécution du contrat</strong> : fourniture des services de l'application</li>
                  <li><strong>Obligation légale</strong> : respect des dispositions légales applicables</li>
                  <li><strong>Intérêt légitime</strong> : amélioration des services et sécurité</li>
                </ul>
              </div>
            </section>

            {/* 5. Destinataires des données */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">5. Destinataires des Données</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>Les données à caractère personnel sont strictement destinées à :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>L'utilisateur lui-même</li>
                  <li>L'association OpenMeta</li>
                  <li>Les salariés de la société Interaction Healthcare chargés de développer l'application, dans la limite de leurs attributions</li>
                  <li>Les salariés strictement habilités de l'hébergeur (OVH) dans la limite de leurs attributions</li>
                </ul>
                
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded mt-3">
                  <p className="font-semibold text-green-300">✅ Engagement de confidentialité</p>
                  <p>Les données à caractère personnel ne seront transmises à aucun tiers non autorisé, sans l'accord de l'Utilisateur.</p>
                </div>
              </div>
            </section>

            {/* 6. Durée de conservation */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">6. Durée de Conservation</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>Les données à caractère personnel sont conservées pendant :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>Données de compte</strong> : Pendant toute la durée d'utilisation de l'Application</li>
                  <li><strong>Après suppression du compte</strong> : Anonymisation et archivage conformément aux exigences légales</li>
                  <li><strong>Données d'analyse</strong> : Sous forme anonymisée pour études statistiques</li>
                </ul>
              </div>
            </section>

            {/* 7. Sécurité des données */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">7. Sécurité des Données</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>OpenMeta met en œuvre toutes les mesures de sécurité techniques et organisationnelles appropriées pour garantir la protection et la sécurité de vos données :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Chiffrement des données sensibles (mots de passe hashés en SHA256)</li>
                  <li>Authentification JWT sécurisée</li>
                  <li>Accès restreint aux données (contrôle d'accès strict)</li>
                  <li>Hébergement sécurisé chez OVH (France)</li>
                  <li>Surveillance et logs de sécurité</li>
                  <li>Mises à jour régulières de sécurité</li>
                </ul>
              </div>
            </section>

            {/* 8. Vos droits */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">8. Vos Droits</h2>
              <div className="space-y-3 text-[hsl(var(--text-dim))]">
                <p>Conformément à la loi Informatique et Libertés et au RGPD, vous disposez des droits suivants :</p>
                
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-cyan-300 mb-1">✓ Droit d'accès</p>
                    <p className="text-xs">Obtenir une copie de vos données</p>
                  </div>
                  
                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-cyan-300 mb-1">✓ Droit de rectification</p>
                    <p className="text-xs">Corriger vos données inexactes</p>
                  </div>
                  
                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-cyan-300 mb-1">✓ Droit à l'effacement</p>
                    <p className="text-xs">Supprimer vos données ("droit à l'oubli")</p>
                  </div>
                  
                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-cyan-300 mb-1">✓ Droit à la portabilité</p>
                    <p className="text-xs">Récupérer vos données dans un format structuré</p>
                  </div>
                  
                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-cyan-300 mb-1">✓ Droit d'opposition</p>
                    <p className="text-xs">Vous opposer au traitement de vos données</p>
                  </div>
                  
                  <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
                    <p className="font-semibold text-cyan-300 mb-1">✓ Droit à la limitation</p>
                    <p className="text-xs">Limiter le traitement dans certains cas</p>
                  </div>
                </div>

                <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded mt-4">
                  <p className="font-semibold text-cyan-300 mb-2">Comment exercer vos droits ?</p>
                  <p className="mb-2">Pour exercer vos droits, contactez-nous en fournissant une copie d'un justificatif d'identité :</p>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-cyan-400" />
                    <a href="mailto:support@openmeta.fr" className="text-cyan-400 hover:underline font-semibold">support@openmeta.fr</a>
                  </div>
                </div>
              </div>
            </section>

            {/* 9. Cookies et traceurs */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">9. Cookies et Traceurs</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>L'Application utilise des cookies et technologies similaires pour :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Maintenir votre session de connexion</li>
                  <li>Mémoriser vos préférences</li>
                  <li>Analyser l'utilisation de l'application (de manière anonyme)</li>
                </ul>
                <p className="mt-2">Vous pouvez gérer les cookies depuis les paramètres de votre navigateur.</p>
              </div>
            </section>

            {/* 10. Données non personnelles */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">10. Partage de Données Non Personnelles</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>L'Utilisateur accepte que OpenMeta puisse partager des informations non personnelles relatives à l'utilisation des Services (données agrégées, statistiques anonymes) qui ne permettent pas l'identification des utilisateurs en particulier.</p>
                <p>Ces données peuvent être utilisées pour :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Analyser les tendances d'utilisation</li>
                  <li>Améliorer les services</li>
                  <li>Réaliser des études statistiques</li>
                </ul>
              </div>
            </section>

            {/* 11. Réclamation CNIL */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">11. Droit de Réclamation</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>Vous disposez du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) si vous estimez que vos droits ne sont pas respectés :</p>
                <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded mt-2">
                  <p className="font-semibold text-[hsl(var(--text))]">CNIL</p>
                  <p>3 Place de Fontenoy</p>
                  <p>TSA 80715</p>
                  <p>75334 PARIS CEDEX 07</p>
                  <p className="mt-2">Tél. : 01 53 73 22 22</p>
                  <p>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">www.cnil.fr</a></p>
                </div>
              </div>
            </section>

            {/* 12. Modifications */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">12. Modifications de la Politique</h2>
              <p className="text-[hsl(var(--text-dim))]">
                OpenMeta se réserve le droit de modifier la présente politique de confidentialité à tout moment. Les utilisateurs seront informés de toute modification substantielle. Il est recommandé de consulter régulièrement cette page.
              </p>
            </section>

            {/* Contact DPO */}
            <section className="pt-4 border-t border-[hsl(var(--border))]">
              <h2 className="text-xl font-bold text-cyan-300 mb-3">Délégué à la Protection des Données</h2>
              <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded">
                <p className="text-[hsl(var(--text-dim))] mb-2">Pour toute question concernant vos données personnelles, vous pouvez contacter notre délégué à la protection des données :</p>
                <a href="mailto:support@openmeta.fr" className="text-cyan-400 hover:underline flex items-center gap-2 font-semibold">
                  <Mail className="w-4 h-4" />
                  support@openmeta.fr
                </a>
              </div>
            </section>

            <p className="text-xs text-[hsl(var(--text-dim))] text-center pt-6">
              Dernière mise à jour : Décembre 2024
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
